// =============================================================================
// NewEmployeeScreen — /people/new
//
// Bare-minimum admin-side employee creation form. James asked for this
// because new staff couldn't be added before the invite flow ran — the
// invite modal's "Link to employee" picker only shows existing rows.
//
// Flow: manager+ fills the form → row inserts into `employees` →
// redirects to /people/{new_id} where they can edit further.
//
// Fields are intentionally minimal:
//   * first/last/email/mobile  — identity
//   * date_of_birth (optional) — payroll/super
//   * employment_type          — drives RLS + invite shape
//   * primary_role             — used for nav scoping later
//   * subcontractor_company_id — required for subbie/labour-hire types
//
// Anything else (NOK, address, banking) edits live on /people/:id once
// the row exists. Keeps this form quick.
// =============================================================================

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, UserPlus, AlertTriangle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Spinner } from "@/components/ui";

type EmploymentType =
  "full_time" | "part_time" | "casual" | "subcontractor" | "labour_hire" | "director";

const EMPLOYMENT_TYPES: { value: EmploymentType; label: string }[] = [
  { value: "full_time",     label: "Full time" },
  { value: "part_time",     label: "Part time" },
  { value: "casual",        label: "Casual" },
  { value: "labour_hire",   label: "Labour hire (ABN)" },
  { value: "subcontractor", label: "Subcontractor (Supplier company)" },
  { value: "director",      label: "Director" },
];

const REQUIRES_COMPANY: EmploymentType[] = ["subcontractor", "labour_hire"];

interface SubbieCompanyOption {
  subcontractor_company_id: string;
  legal_name:               string;
  trading_name:             string | null;
}

export default function NewEmployeeScreen() {
  const user     = useRequireAuth();
  const navigate = useNavigate();

  const [firstName,   setFirstName]   = useState("");
  const [lastName,    setLastName]    = useState("");
  const [email,       setEmail]       = useState("");
  const [mobile,      setMobile]      = useState("");
  const [dob,         setDob]         = useState("");
  const [employmentType, setEmploymentType] = useState<EmploymentType>("full_time");
  const [roleName,    setRoleName]    = useState<string>("field");
  const [companyId,   setCompanyId]   = useState<string>("");
  const [error,       setError]       = useState<string | null>(null);

  // Companies list for the subbie/labour-hire types
  const { data: companies = [] } = useQuery({
    queryKey: ["new_employee_companies"],
    enabled: REQUIRES_COMPANY.includes(employmentType),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subcontractor_companies")
        .select("subcontractor_company_id, legal_name, trading_name")
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as SubbieCompanyOption[];
    },
  });

  // Roles list — five operational roles per the schema seed
  const { data: roles = [] } = useQuery({
    queryKey: ["new_employee_roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles")
        .select("role_name, display_name, sort_order")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as { role_name: string; display_name: string }[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      // Cross-field validation — match the DB check in app.complete_onboarding
      if (REQUIRES_COMPANY.includes(employmentType) && !companyId) {
        throw new Error(
          `${employmentType === "subcontractor" ? "Subcontractor" : "Labour-hire"} ` +
          `employees must be linked to a subcontractor company.`,
        );
      }
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error("First and last name are required.");
      }

      const { data, error } = await supabase
        .from("employees")
        .insert({
          first_name:               firstName.trim(),
          last_name:                lastName.trim(),
          email:                    email.trim() || null,
          mobile:                   mobile.trim() || null,
          employment_type:          employmentType,
          primary_role_name:        roleName,
          subcontractor_company_id: REQUIRES_COMPANY.includes(employmentType)
            ? companyId : null,
          is_active:                true,
        })
        .select("employee_id")
        .single();
      if (error) throw error;
      const employeeId = data!.employee_id as string;
      // T-446 — DOB now lives in employee_private; set it via the RPC (handles gating).
      if (dob) {
        const { error: pErr } = await supabase.schema("app").rpc("update_employee_profile", {
          p_employee_id: employeeId,
          p_payload: { date_of_birth: dob },
        });
        if (pErr) throw pErr;
      }
      return employeeId;
    },
    onSuccess: (employeeId) => {
      navigate(`/people/${employeeId}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Create failed";
      setError(msg);
    },
  });

  // Role guard
  if (!hasRole(user, "manager")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Only managers and directors can add new employees.
          </div>
        </Card>
      </div>
    );
  }

  const requiresCompany = REQUIRES_COMPANY.includes(employmentType);

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-16">
      <Link
        to="/people"
        className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2"
      >
        <ArrowLeft size={14} /> Back to People
      </Link>

      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Add new employee</h1>
        <div className="text-text-muted text-[13px] mt-1">
          Create the personnel record. You can edit address, banking, NOK
          and other details on the employee page after this saves. To grant
          a login, send an invite from <em>Admin → Users</em> and link it to
          this employee.
        </div>
      </div>

      <Card>
        <div className="p-4 md:p-5 grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <Input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </Field>
            <Field label="Last name *">
              <Input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </Field>
            <Field label="Mobile">
              <Input
                type="tel"
                value={mobile}
                onChange={e => setMobile(e.target.value)}
                placeholder="0400 000 000"
                autoComplete="tel"
              />
            </Field>
          </div>

          <Field label="Date of birth">
            <Input
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              autoComplete="bday"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Employment type *">
              <select
                value={employmentType}
                onChange={e => setEmploymentType(e.target.value as EmploymentType)}
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              >
                {EMPLOYMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Primary role *">
              <select
                value={roleName}
                onChange={e => setRoleName(e.target.value)}
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              >
                {roles.map(r => (
                  <option key={r.role_name} value={r.role_name}>
                    {r.display_name ?? r.role_name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {requiresCompany && (
            <Field label="Subcontractor company *" hint="Required for labour-hire and subcontractor employees.">
              <select
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              >
                <option value="">— pick a company —</option>
                {companies.map(c => (
                  <option key={c.subcontractor_company_id} value={c.subcontractor_company_id}>
                    {c.trading_name ?? c.legal_name}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </Card>

      {error && (
        <Card className="mt-4 border-danger/30">
          <div className="p-4 flex items-start gap-3 text-sm text-danger">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
            <span>{error}</span>
          </div>
        </Card>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <Link to="/people">
          <Button variant="ghost" disabled={create.isPending}>Cancel</Button>
        </Link>
        <Button
          variant="primary"
          icon={UserPlus}
          onClick={() => { setError(null); create.mutate(); }}
          disabled={create.isPending}
        >
          {create.isPending ? "Saving…" : "Create employee"}
        </Button>
      </div>
    </div>
  );
}
